import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { notification } from '@antoree/ant-ui';
import { FormattedMessage, useIntl } from 'react-intl';
// S3 Upload - COMMENTED OUT (only using R2)
// import {
//   initS3VideoUpload,
//   uploadVideoToS3,
//   completeS3VideoUpload,
// } from 'services/session/s3VideoUpload';
import { RETRIEVE_SESSION_URL } from 'services';
import { API_SERVICE_URL } from 'configs/env.conf';

interface UseVideoUploadParams {
  sessionId: number;
  setUploadProgress: (progress: number) => void;
  setIsUploading: (isUploading: boolean) => void;
  setVideoInputKey: (fn: (prev: number) => number) => void;
  setVideoUrlToIdMap: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  // S3 Upload - COMMENTED OUT (only using R2)
  // updateS3VideoUrls: React.Dispatch<React.SetStateAction<string[]>>;
}

interface UseVideoUploadReturn {
  uploadVideo: (videoFile: File) => Promise<void>;
}

/**
 * Hook to handle video upload to R2 (S3 is commented out)
 */
export const useVideoUpload = ({
  sessionId,
  setUploadProgress,
  setIsUploading,
  setVideoInputKey,
  setVideoUrlToIdMap,
}: // updateS3VideoUrls, // S3 Upload - COMMENTED OUT
UseVideoUploadParams): UseVideoUploadReturn => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem('token');
  const intl = useIntl();

  const logRecordEvent = useCallback(
    async (eventType: string, errorMessage?: string, metadata?: any) => {
      try {
        if (!token) return;

        await fetch(`${API_SERVICE_URL}/video-upload/record-log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            unitId: sessionId,
            eventType,
            errorMessage,
            metadata: {
              ...metadata,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
          }),
        });
      } catch (err) {
        console.warn('[VideoUpload] Failed to log event:', eventType, err);
      }
    },
    [sessionId, token],
  );

  /**
   * R2 Upload (primary method now)
   */
  const handleR2Upload = useCallback(
    async (videoFile: File) => {
      try {
        setUploadProgress(10);
        void logRecordEvent('upload_started', undefined, {
          fileSize: videoFile.size,
          mimeType: videoFile.type || 'video/webm',
        });

        // 1. Init Upload
        const initResponse = await fetch(
          `${API_SERVICE_URL}/video-upload/init`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              unitId: sessionId,
              mimeType: videoFile.type || 'video/webm',
            }),
          },
        );

        if (!initResponse.ok) {
          throw new Error('Init upload failed');
        }
        const initData = await initResponse.json();
        const uploadedVideoId = initData.data.id;

        setUploadProgress(20);

        // 2. Direct Upload to R2
        const uploadResponse = await fetch(initData.data.upload_url, {
          method: 'PUT',
          body: videoFile,
          headers: {
            'Content-Type': videoFile.type || 'video/webm',
          },
        });

        if (!uploadResponse.ok) throw new Error('R2 upload PUT failed');

        setUploadProgress(90);

        // 3. Complete Upload
        const completeResponse = await fetch(
          `${API_SERVICE_URL}/video-upload/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: uploadedVideoId,
            }),
          },
        );

        if (!completeResponse.ok) {
          throw new Error('Complete upload failed');
        }

        // Show success notification
        notification.success({
          title: intl.formatMessage({ defaultMessage: 'Upload video success' }),
          text: intl.formatMessage({
            defaultMessage: 'Video has been uploaded successfully.',
          }),
        });

        // Update cache to ensure video appears in preview
        const completeData = await completeResponse.json();
        if (completeData.data?.unitVideo) {
          queryClient.setQueryData(
            [RETRIEVE_SESSION_URL(sessionId)],
            (old: any) => {
              if (!old) return old;
              return {
                ...old,
                data: {
                  ...old.data,
                  videoUrl: completeData.data.unitVideo,
                },
              };
            },
          );
        }

        // Refetch to ensure preview shows
        await queryClient.refetchQueries([RETRIEVE_SESSION_URL(sessionId)]);

        setIsUploading(false);
        setUploadProgress(100);
        setVideoInputKey(prev => prev + 1); // Clear input after success

        void logRecordEvent('upload_completed', undefined, {
          fileSize: videoFile.size,
          mimeType: videoFile.type || 'video/webm',
        });
      } catch (error) {
        console.error('R2 video upload error:', error);
        setIsUploading(false);
        setUploadProgress(0);

        void logRecordEvent(
          'server_error',
          error instanceof Error ? error.message : 'Unknown error',
          {
            fileSize: videoFile.size,
            mimeType: videoFile.type || 'video/webm',
          },
        );
        notification.error({
          title: intl.formatMessage({ defaultMessage: 'Upload video failed' }),
          text: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    },
    [
      sessionId,
      token,
      queryClient,
      setUploadProgress,
      setIsUploading,
      setVideoInputKey,
    ],
  );

  // ========== S3 Upload - COMMENTED OUT (only using R2 now) ==========
  // /**
  //  * S3 Upload (primary method)
  //  */
  // const handleS3Upload = useCallback(
  //   async (videoFile: File) => {
  //     try {
  //       setUploadProgress(10);
  //
  //       // 1. Initialize S3 Upload
  //       const initData = await initS3VideoUpload({
  //         unitId: sessionId,
  //         mimeType: videoFile.type || 'video/mp4',
  //       });
  //
  //       setUploadProgress(20);
  //
  //       // 2. Upload Video to S3 using presigned URL
  //       if (!initData.upload_url) {
  //         throw new Error('Upload URL is missing from init response');
  //       }
  //
  //       await uploadVideoToS3(initData.upload_url, videoFile, progress => {
  //         // Map upload progress from 20% to 90% (20% init + 70% upload)
  //         const mappedProgress = 20 + Math.round((progress / 100) * 70);
  //         setUploadProgress(mappedProgress);
  //       });
  //
  //       setUploadProgress(90);
  //
  //       // 3. Complete S3 Upload
  //       const completeData = await completeS3VideoUpload({
  //         id: initData.id,
  //         fileSize: videoFile.size,
  //       });
  //
  //       notification.success({
  //         title: <FormattedMessage defaultMessage="Upload video success" />,
  //         text: (
  //           <FormattedMessage defaultMessage="Video will be available on the system for 24 hours after upload. After that, it will be automatically deleted according to the new policy." />
  //         ),
  //       });
  //
  //       // Extract video_url from response
  //       const s3VideoUrl = completeData.video_url;
  //
  //       // Store ID mapping by URL
  //       if (s3VideoUrl) {
  //         setVideoUrlToIdMap(prev => ({
  //           ...prev,
  //           [s3VideoUrl]: initData.id,
  //         }));
  //       }
  //
  //       // Store S3 video URL for deletion
  //       if (
  //         s3VideoUrl &&
  //         typeof s3VideoUrl === 'string' &&
  //         s3VideoUrl.trim().length > 0
  //       ) {
  //         updateS3VideoUrls(prev => {
  //           // Avoid duplicates
  //           if (prev.includes(s3VideoUrl)) {
  //             return prev;
  //           }
  //           return [...prev, s3VideoUrl];
  //         });
  //       }
  //
  //       // Update cache immediately to show preview
  //       if (completeData.unitVideo && Array.isArray(completeData.unitVideo)) {
  //         queryClient.setQueryData(
  //           [RETRIEVE_SESSION_URL(sessionId)],
  //           (old: any) => {
  //             if (!old) return old;
  //             return {
  //               ...old,
  //               data: {
  //                 ...old.data,
  //                 videoUrl: completeData.unitVideo,
  //               },
  //             };
  //           },
  //         );
  //       }
  //       // Refetch immediately to ensure preview shows
  //       await queryClient.refetchQueries([RETRIEVE_SESSION_URL(sessionId)]);
  //
  //       setIsUploading(false);
  //       setUploadProgress(100);
  //       setVideoInputKey(prev => prev + 1); // Clear input after success
  //     } catch (error) {
  //       console.error('S3 video upload error:', error);
  //       setIsUploading(false);
  //       setUploadProgress(0);
  //       notification.error({
  //         title: <FormattedMessage defaultMessage="Upload video failed" />,
  //         text: error instanceof Error ? error.message : 'Unknown error',
  //       });
  //       throw error;
  //     }
  //   },
  //   [
  //     sessionId,
  //     setUploadProgress,
  //     setIsUploading,
  //     setVideoInputKey,
  //     setVideoUrlToIdMap,
  //     updateS3VideoUrls,
  //     queryClient,
  //   ],
  // );
  // ========== END S3 Upload ==========

  /**
   * Main upload function - uploads to R2 only (S3 is commented out)
   */
  const uploadVideo = useCallback(
    async (videoFile: File) => {
      // Upload to R2 only
      await handleR2Upload(videoFile);
    },
    [handleR2Upload],
  );

  return { uploadVideo };
};
